/**
 * Editable patient-status column using the medonc BadgeDropdown pattern.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useMemo, useState } from "react";
import BadgeDropdown from "../../../components/badgeDropdown/badgeDropdown";
import type { BadgeWithIconProps } from "../../../data-model/badgeWithIconProps";
import { toUserFacingApiErrorMessage } from "../../../utils/apiErrorMessage";
import { toPatientStatusBadgeProps } from "../../../utils/patientStatusBadges";

interface PatientStatusBadgeCellProps {
  /** Patient MRN for the row being updated. */
  mrn: string;
  /** Current persisted patient status value. */
  value: string;
  /** Full dropdown list with design colors applied. */
  badgeList: BadgeWithIconProps[];
  /**
   * Persists a new patient status for this MRN.
   *
   * @param mrn - Patient medical record number
   * @param patientStatus - New status enum value
   */
  onChange: (mrn: string, patientStatus: string) => Promise<void>;
}

/**
 * Render a portaled badge dropdown that patches patient workflow status.
 *
 * @param props - {@link PatientStatusBadgeCellProps}
 * @returns Patient status badge dropdown cell
 */
export function PatientStatusBadgeCell({
  mrn,
  value,
  badgeList,
  onChange,
}: PatientStatusBadgeCellProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const selectedBadge = useMemo(() => {
    return (
      badgeList.find((badge) => badge.id === value) ??
      toPatientStatusBadgeProps(value)
    );
  }, [badgeList, value]);

  /**
   * Persist the newly selected status and lock the control while saving.
   *
   * @param patientStatus - Selected patient status value
   */
  const handleSelect = async (patientStatus: string) => {
    if (patientStatus === value || isSaving) return;
    setIsSaving(true);
    try {
      await onChange(mrn, patientStatus);
    } catch (error) {
      const message = toUserFacingApiErrorMessage(
        error,
        "Failed to update status",
      );
      toast.error(message, "Failed to update status");
      setResetKey((key) => key + 1);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={isSaving ? "pointer-events-none opacity-60" : undefined}
      onClick={(event) => event.stopPropagation()}
    >
      <BadgeDropdown
        key={resetKey}
        selectedBadge={selectedBadge}
        badgeList={badgeList}
        onClick={(id) => void handleSelect(id)}
        shouldUsePortal
      />
    </div>
  );
}

export default PatientStatusBadgeCell;
